import React, { memo } from 'react';
import { Row, Col, Table } from 'react-bootstrap';
import { formatInTimeZone } from 'date-fns-tz';
import officialscoresheetStyles from '../../officialscoresheet.module.css';
import { Clock } from '../shared/clock';

const {
  header,
  row,
  virtiusTitle,
  sessionInfo,
  sessionInfoLabel,
  sessionInfoValue,
  body,
  footer,
  subFooter,
  timestamp,
  lineupForm,
  rotationSummaryTable,
  rotationSummaryCell,
  rotationSummaryHeader: headerClass,
  rotationNumberCell,
  rotationSummaryCompact,
  rotationSummaryVeryCompact,
} = officialscoresheetStyles;

function RotationSummaryHeader({
  sessionName,
  date,
  timezone,
  location,
  combineKey,
  combineSession,
}) {
  return (
    <section className={header}>
      <h1>
        <span className={virtiusTitle}>
          <span>V</span>irtius&nbsp;
        </span>
        <span style={{ fontWeight: '700' }}>Competition Form</span>
      </h1>
      <h2>ROTATION SUMMARY</h2>
      <section>
        <Row className={row}>
          <Col className={sessionInfo}>
            <div className={sessionInfoLabel}>
              Session{combineKey ? '(s)' : null}:&nbsp;
            </div>
            <div className={sessionInfoValue}>
              {sessionName}
              {combineKey ? ` + ${combineSession?.session?.name}` : null}
            </div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={sessionInfo}>
            <div className={sessionInfoLabel}>Date: </div>
            <div className={sessionInfoValue}>{`${formatInTimeZone(
              new Date(date),
              timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
              "M'/'dd'/'yyyy"
            )}`}</div>
          </Col>
        </Row>
        <Row className={row}>
          <Col className={sessionInfo}>
            <div className={sessionInfoLabel}>Location: </div>
            <div className={sessionInfoValue}>{location}</div>
          </Col>
        </Row>
      </section>
    </section>
  );
}

function RotationSummaryTable({ teams, apparatusArray, sessionRosters }) {
  // Build lookup from lineupId to squadStartPosition for consistent ordering
  const squadStartPositionByLineupId = {};
  (sessionRosters || []).forEach((roster) => {
    if (roster.lineupId && !roster._deleted) {
      squadStartPositionByLineupId[roster.lineupId] =
        roster.squadStartPosition ?? 0;
    }
  });

  // Build rotation data structure from teams array (like lineup verification does)
  const rotationData = {};

  // Use teams array to get lineup data (same approach as lineup verification)
  // Use scheduledRotation (config-based) for display, not rotation (which may be from routine data)
  teams.forEach((team) => {
    team.events.forEach((event) => {
      const rotationNum = event.scheduledRotation ?? event.rotation;
      const apparatus = event.apparatus;

      if (rotationNum !== undefined && apparatus) {
        rotationData[rotationNum] = rotationData[rotationNum] || {};

        // Get lineup for this rotation from event
        const lineup = event.lineup || [];
        rotationData[rotationNum][apparatus] =
          rotationData[rotationNum][apparatus] || [];

        const lineupEntry = {
          team: team,
          squadStartPosition: squadStartPositionByLineupId[team.lineupId] ?? 0,
          athletes: lineup.map((athlete) => ({
            number: athlete.number || '',
            name: athlete.name,
          })),
        };

        rotationData[rotationNum][apparatus].push(lineupEntry);
      }
    });
  });

  const rotationNumbers = Object.keys(rotationData).sort((a, b) => a - b);

  // Calculate dynamic styling based on number of apparatus
  const apparatusCount = apparatusArray.length;
  let tableClass = rotationSummaryTable;

  // Add compact classes for more apparatus
  if (apparatusCount >= 7) {
    tableClass = `${tableClass} ${rotationSummaryVeryCompact}`;
  } else if (apparatusCount >= 5) {
    tableClass = `${tableClass} ${rotationSummaryCompact}`;
  }

  return (
    <Table className={tableClass}>
      <thead>
        <tr>
          <th className={`${headerClass} ${rotationNumberCell}`}>#</th>
          {apparatusArray.map((app) => (
            <th key={`header_${app}`} className={headerClass}>
              {app}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rotationNumbers.map((rotNum) => {
          return (
            <tr key={`rotation_${rotNum}`}>
              <td className={rotationNumberCell}>{rotNum}</td>
              {apparatusArray.map((app) => {
                const cellData = (rotationData[rotNum][app] || []).sort(
                  (a, b) =>
                    (a.squadStartPosition ?? 0) - (b.squadStartPosition ?? 0)
                );
                return (
                  <td
                    key={`rotation_${rotNum}_${app}`}
                    className={rotationSummaryCell}
                  >
                    {cellData.length > 0
                      ? cellData.map((lineupEntry, entryIdx) => {
                          if (
                            !lineupEntry?.athletes ||
                            lineupEntry.athletes.length === 0
                          ) {
                            return null;
                          }

                          return (
                            <div key={`${rotNum}_${app}_${entryIdx}`}>
                              <div
                                style={{
                                  fontWeight: 'bold',
                                  marginBottom: '6pt',
                                }}
                              >
                                {lineupEntry.team?.displayName ||
                                  lineupEntry.team?.name}
                              </div>
                              {lineupEntry.athletes.map((athlete, idx) => (
                                <div
                                  key={`${rotNum}_${app}_${entryIdx}_${idx}`}
                                >
                                  {athlete.number || ''} {athlete.name}
                                </div>
                              ))}
                              {entryIdx < cellData.length - 1 ? (
                                <div style={{ margin: '8pt 0' }} />
                              ) : null}
                            </div>
                          );
                        })
                      : null}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

function RotationSummaryBody({ teams, apparatusArray, sessionRosters }) {
  return (
    <main className={body}>
      <Row className={row}>
        <Col>
          <RotationSummaryTable
            teams={teams}
            apparatusArray={apparatusArray}
            sessionRosters={sessionRosters}
          />
        </Col>
      </Row>
    </main>
  );
}

function BasicScoresheetFooter({
  page,
  timezone,
  singleSheetOnly,
  sheetOptionsLength,
}) {
  const copyrightTimePageFooter = (page) => {
    return (
      <section className={subFooter}>
        <Row>
          <Col className={timestamp}>
            <span>
              © {new Date().getFullYear()} Virtius&nbsp;&nbsp;»&nbsp;&nbsp;
            </span>
            <Clock timezone={timezone} />
            <span>
              &nbsp;&nbsp;»&nbsp;&nbsp;Page {singleSheetOnly ? 1 : page} of{' '}
              {singleSheetOnly ? 1 : sheetOptionsLength}
            </span>
          </Col>
        </Row>
      </section>
    );
  };

  return <section className={footer}>{copyrightTimePageFooter(page)}</section>;
}

export const RotationSummaryScoreSheet = memo(_RotationSummaryScoreSheet);

function _RotationSummaryScoreSheet({
  page,
  sessionName,
  date,
  timezone,
  location,
  combineKey,
  combineSession,
  teams,
  apparatusArray,
  singleSheetOnly,
  sheetOptionsLength,
  sessionRosters,
}) {
  return (
    <div className={lineupForm}>
      <RotationSummaryHeader
        sessionName={sessionName}
        date={date}
        timezone={timezone}
        location={location}
        combineKey={combineKey}
        combineSession={combineSession}
      />
      <RotationSummaryBody
        teams={teams}
        apparatusArray={apparatusArray}
        sessionRosters={sessionRosters}
      />
      <BasicScoresheetFooter
        page={page}
        timezone={timezone}
        singleSheetOnly={singleSheetOnly}
        sheetOptionsLength={sheetOptionsLength}
      />
    </div>
  );
}
